import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchAutopilotNotificationProviders,
  fetchAutopilotNotificationTransactions,
  adjustAutopilotNotificationCredits,
  type AutopilotNotificationProvider,
  type AutopilotNotificationTransaction,
} from '@/lib/api/autopilot';
import { useTenantSlug } from '@/stores/tenantStore';

export const useAutopilotNotificationProviders = () => {
  const tenantSlug = useTenantSlug();
  return useQuery({
    queryKey: ['autopilot', 'notifications', 'providers', tenantSlug],
    enabled: Boolean(tenantSlug),
    queryFn: ({ signal }) => {
      if (!tenantSlug) {
        return Promise.resolve([] as AutopilotNotificationProvider[]);
      }
      return fetchAutopilotNotificationProviders({ tenantSlug, signal });
    },
  });
};

export const useAutopilotNotificationTransactions = (
  channel: 'sms' | 'email' | null,
  limit = 50,
) => {
  const tenantSlug = useTenantSlug();
  return useQuery({
    queryKey: ['autopilot', 'notifications', 'transactions', tenantSlug, channel, limit],
    enabled: Boolean(tenantSlug && channel),
    queryFn: ({ signal }) => {
      if (!tenantSlug || !channel) {
        return Promise.resolve([] as AutopilotNotificationTransaction[]);
      }
      return fetchAutopilotNotificationTransactions({ tenantSlug, channel, limit, signal });
    },
  });
};

export const useAdjustAutopilotNotificationCredits = () => {
  const tenantSlug = useTenantSlug();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      channel,
      amountCents,
      type,
      note,
    }: {
      channel: 'sms' | 'email';
      amountCents: number;
      type?: 'credit' | 'adjustment';
      note?: string | null;
    }) => {
      if (!tenantSlug) {
        throw new Error('Tenant slug required');
      }
      await adjustAutopilotNotificationCredits({
        tenantSlug,
        channel,
        amountCents,
        type,
        note,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['autopilot', 'notifications', 'providers'] });
      void queryClient.invalidateQueries({ queryKey: ['autopilot', 'notifications', 'transactions'] });
    },
  });
};
